import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import CierrePeriodoWidget from '../components/CierrePeriodoWidget';
import {
  BarChart3,
  ClipboardList,
  ArrowLeft,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  TrendingUp,
  Users,
  Calendar,
  ArrowRight,
  XCircle,
  Loader2,
  Plus
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const TramitesDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, configRes] = await Promise.all([
        api.get('/tramites/dashboard'),
        api.get('/tramites/config')
      ]);
      setDashboard(dashboardRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#8b5cf6', '#3b82f6', '#f59e0b', '#10b981', '#6b7280', '#ef4444'];

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'NUEVO': return '#8b5cf6';
      case 'EN_PROCESO': return '#3b82f6';
      case 'EN_REVISION': return '#f59e0b';
      case 'COMPLETADO': return '#10b981';
      case 'CANCELADO': return '#6b7280';
      default: return '#94a3b8';
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'URGENTE': return '#ef4444';
      case 'ALTA': return '#f59e0b';
      case 'MEDIA': return '#3b82f6';
      case 'BAJA': return '#94a3b8';
      default: return '#94a3b8';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  // Preparar datos para gráficos
  const estadosData = Object.entries(dashboard?.por_estado || {}).map(([estado, count]) => ({
    name: estado.replace('_', ' '),
    value: count,
    color: getEstadoColor(estado)
  }));

  const prioridadData = Object.entries(dashboard?.por_prioridad || {}).map(([prioridad, count]) => ({
    name: prioridad,
    value: count,
    color: getPrioridadColor(prioridad)
  }));

  const origenData = Object.entries(dashboard?.por_origen || {}).map(([origen, count]) => ({
    name: config?.origenes?.find(o => o.id === origen)?.nombre || origen,
    value: count,
    color: config?.origenes?.find(o => o.id === origen)?.color || '#94a3b8'
  }));

  return (
    <div className="p-6 space-y-6" data-testid="tramites-dashboard-page">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <BarChart3 size={24} className="text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Dashboard de Trámites</h1>
              <p className="text-slate-600">Métricas y estado general</p>
            </div>
          </div>
        </div>
        {/* Action buttons */}
        <div className="flex gap-2">
          <Button onClick={() => navigate('/m/tramites/tramites?new=true')}>
            <Plus size={16} className="mr-2" />
            Nuevo Trámite
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw size={16} className="mr-2" />
            Actualizar
          </Button>
          <Button variant="outline" onClick={() => navigate('/m/tramites/tramites')}>
            <ClipboardList size={16} className="mr-2" />
            Ver Lista
          </Button>
        </div>
      </div>

      {/* Widget de Cierre de Período - Al inicio */}
      <CierrePeriodoWidget />

      {/* KPIs principales */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="p-5 bg-white border-l-4 border-l-indigo-500">
          <div className="flex items-center gap-3">
            <ClipboardList size={24} className="text-indigo-500" />
            <div>
              <p className="text-3xl font-bold text-slate-900">{dashboard?.totales?.total || 0}</p>
              <p className="text-sm text-slate-500">Total Trámites</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 bg-white border-l-4 border-l-blue-500">
          <div className="flex items-center gap-3">
            <RefreshCw size={24} className="text-blue-500" />
            <div>
              <p className="text-3xl font-bold text-slate-900">{dashboard?.totales?.activos || 0}</p>
              <p className="text-sm text-slate-500">Activos</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 bg-white border-l-4 border-l-purple-500">
          <div className="flex items-center gap-3">
            <Users size={24} className="text-purple-500" />
            <div>
              <p className="text-3xl font-bold text-slate-900">{dashboard?.totales?.mis_pendientes || 0}</p>
              <p className="text-sm text-slate-500">Mis Pendientes</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 bg-amber-50 border-l-4 border-l-amber-500">
          <div className="flex items-center gap-3">
            <Clock size={24} className="text-amber-500" />
            <div>
              <p className="text-3xl font-bold text-amber-700">{dashboard?.totales?.por_vencer || 0}</p>
              <p className="text-sm text-amber-600">Por Vencer (7d)</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 bg-red-50 border-l-4 border-l-red-500">
          <div className="flex items-center gap-3">
            <AlertTriangle size={24} className="text-red-500" />
            <div>
              <p className="text-3xl font-bold text-red-700">{dashboard?.totales?.vencidos || 0}</p>
              <p className="text-sm text-red-600">Vencidos</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-5 bg-green-50 border-l-4 border-l-green-500">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-green-500" />
            <div>
              <p className="text-3xl font-bold text-green-700">{dashboard?.totales?.completados || 0}</p>
              <p className="text-sm text-green-600">Completados</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Por Estado */}
        <Card className="p-5 bg-white">
          <h3 className="font-medium text-slate-900 mb-4">Por Estado</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={estadosData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {estadosData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Por Prioridad */}
        <Card className="p-5 bg-white">
          <h3 className="font-medium text-slate-900 mb-4">Por Prioridad</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={prioridadData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {prioridadData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Por Origen */}
        <Card className="p-5 bg-white">
          <h3 className="font-medium text-slate-900 mb-4">Por Origen</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={origenData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {origenData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Próximos a vencer */}
      <Card className="p-5 bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-slate-900 flex items-center gap-2">
            <Clock size={18} className="text-amber-500" />
            Próximos a Vencer
          </h3>
          <Button variant="outline" size="sm" onClick={() => navigate('/m/compliance/tramites?tab=por_vencer')}>
            Ver todos
            <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
        
        {dashboard?.proximos_vencer?.length === 0 ? (
          <p className="text-slate-500 text-center py-4">No hay trámites próximos a vencer</p>
        ) : (
          <div className="space-y-2">
            {dashboard?.proximos_vencer?.map((t) => (
              <div 
                key={t.id} 
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                onClick={() => navigate(`/m/compliance/tramites/${t.id}`)}
              >
                <div className="flex items-center gap-3">
                  <Badge style={{ backgroundColor: (config?.origenes?.find(o => o.id === t.origen)?.color || '#6b7280') + '20', color: config?.origenes?.find(o => o.id === t.origen)?.color }}>
                    {config?.origenes?.find(o => o.id === t.origen)?.nombre || t.origen}
                  </Badge>
                  <span className="font-medium text-slate-900">{t.titulo}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={
                    t.prioridad === 'URGENTE' ? 'bg-red-100 text-red-700' :
                    t.prioridad === 'ALTA' ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }>
                    {t.prioridad}
                  </Badge>
                  <span className="text-sm text-amber-600 font-medium">
                    {new Date(t.fecha_vencimiento).toLocaleDateString('es-ES')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Resumen por estado */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['NUEVO', 'EN_PROCESO', 'EN_REVISION', 'COMPLETADO'].map(estado => {
          const count = dashboard?.por_estado?.[estado] || 0;
          const IconMap = {
            NUEVO: Clock,
            EN_PROCESO: RefreshCw,
            EN_REVISION: AlertTriangle,
            COMPLETADO: CheckCircle
          };
          const Icon = IconMap[estado];
          return (
            <Card 
              key={estado}
              className="p-4 bg-white hover:shadow-md cursor-pointer transition-shadow"
              onClick={() => navigate(`/m/compliance/tramites?estado=${estado}`)}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  estado === 'NUEVO' ? 'bg-purple-100' :
                  estado === 'EN_PROCESO' ? 'bg-blue-100' :
                  estado === 'EN_REVISION' ? 'bg-amber-100' :
                  'bg-green-100'
                }`}>
                  <Icon size={20} className={
                    estado === 'NUEVO' ? 'text-purple-600' :
                    estado === 'EN_PROCESO' ? 'text-blue-600' :
                    estado === 'EN_REVISION' ? 'text-amber-600' :
                    'text-green-600'
                  } />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{count}</p>
                  <p className="text-sm text-slate-500">{estado.replace('_', ' ')}</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

    </div>
  );
};

export default TramitesDashboard;
